import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useLeagueService } from "~pages/Leagues/hooks/useLeagueService.ts";
import { useExtractLeaguesFromScope } from "~pages/Leagues/LeagueListPage/hooks/useExtractLeaguesFromScope.ts";
import { useFilteredLeagues } from "~pages/Leagues/LeagueListPage/hooks/useFilteredLeagues.ts";
import { useGetLeagueTableColumns } from "~pages/Leagues/LeagueListPage/hooks/useGetLeagueTableColumns.tsx";
import { useGetLeagueTableFilters } from "~pages/Leagues/LeagueListPage/hooks/useGetLeagueTableFilters.tsx";
import type { League } from "~types/league.ts";

const LeagueListPage = () => {
  const paginationConfig = useHandlePagination(10, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/leagues/manage/`);
  const [searchText, setSearchText] = useState(location.state?.search || "");

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.leagues.listPage;
  const filterConfig = useGetLeagueTableFilters();
  const { leagues, isLoading, refetch } = useExtractLeaguesFromScope();

  const filteredLeagues = useFilteredLeagues({
    leagues,
    currentFilter: filterConfig.currentFilter,
    searchText,
  });

  const { deleteLeague, isDeletePending } = useLeagueService();

  const handleDelete = useCallback(
    async (leagueId: string) => {
      return await deleteLeague(leagueId);
    },
    [deleteLeague],
  );

  const columns = useGetLeagueTableColumns();

  const getNavigateUrl = (leagueId: string) => `/${customerId}/admin/${sport}/${organizationId}/leagues/manage/${leagueId}`;

  return (
    <DataTable<League>
      title={formatMessage({ id: "leaguesListPage.title" })}
      columns={columns}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => handleDelete(row.leagueId),
            loading: isDeletePending,
          },
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.leagueId)),
          },
        },
      }}
      rowKey={"leagueId"}
      rowNameKey={"nameLocal"}
      dataSource={filteredLeagues}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.leagues`}
      isDataLoading={isLoading}
      handleRefresh={async () => {
        await refetch();
      }}
      handleAdd={handleAdd}
      addButtonIntlKey={"league"}
      filtersConfig={filterConfig}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      permissionNames={[PermissionNames.LEAGUE]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, total: filteredLeagues.length }}
    />
  );
};

export default LeagueListPage;
